/**
 * Help System — shared content loader
 *
 * Fetches help markdown from the app's static assets at runtime, so content can
 * be edited and redeployed WITHOUT rebuilding the Angular app. Caches by
 * (system, locale, topic) and falls back: requested locale → English →
 * not-found stub. Shared by both the artist and gallery systems (each passes
 * its own `system` root: 'artist' | 'gallery').
 */

import { HelpSystemId } from './help.types';

const cache = new Map<string, string>();

function assetPath(system: HelpSystemId, locale: string, topic: string): string {
  return `assets/help/${system}/${locale}/${topic}.md`;
}

async function fetchRaw(path: string): Promise<string | null> {
  try {
    const res = await fetch(path, { cache: 'no-cache' });
    if (!res.ok) return null;
    const text = await res.text();
    // A missing asset on some dev servers returns index.html (200) — guard it.
    if (/^\s*<!doctype html/i.test(text) || /^\s*<html/i.test(text)) return null;
    return text;
  } catch {
    return null;
  }
}

/**
 * Load raw markdown for a topic within a help system, with locale fallback.
 * Always resolves (never throws) — returns a not-found stub if nothing matches.
 */
export async function loadHelpContent(
  system: HelpSystemId,
  topic: string,
  locale: string = 'en',
): Promise<string> {
  const key = `${system}:${locale}:${topic}`;
  if (cache.has(key)) return cache.get(key)!;

  let raw = await fetchRaw(assetPath(system, locale, topic));
  if (raw === null && locale !== 'en') {
    raw = await fetchRaw(assetPath(system, 'en', topic));
  }
  if (raw === null) {
    raw = notFoundStub(topic);
  }

  cache.set(key, raw);
  return raw;
}

function notFoundStub(topic: string): string {
  return `---
title: "Help coming soon"
---

# Help coming soon

The help article for **${topic}** has not been written yet.

[Back to overview](getting-started)
`;
}

/** Clear the in-memory cache (used when the locale changes). */
export function clearHelpCache(): void {
  cache.clear();
}
